import { classNames } from '@vkontakte/vkui';
import isThisYear from 'date-fns/isThisYear';
import parseISO from 'date-fns/parseISO';
import img80 from 'img/quota-landing/blackFriday2025/sale_small.png';
import img81 from 'img/quota-landing/blackFriday2025/sale_small_family.png';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { promotionEndDate } from 'reactApp/appHelpers/featuresHelpers';
import {
    blackFriday2025Tariff,
    blackFriday2025TariffFamily,
    blackFriday2025TariffSpaceEveryone,
} from 'reactApp/appHelpers/featuresHelpers/features/blackFriday2025';
import { isFamilySpaceExpValue } from 'reactApp/appHelpers/featuresHelpers/features/familySpaceToEach';
import {
    multiTariffFamilyTariffListEnable,
    multiTariffSpaceToEachFamilyMember,
} from 'reactApp/appHelpers/featuresHelpers/features/multiTariff';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { getPaySource } from 'reactApp/modules/payment/payment.analytics';
import { openTariffBuy } from 'reactApp/modules/payment/payment.module';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import { getMultiTariffActiveTab } from 'reactApp/modules/quotaLanding/quotaLanding.selector';
import type { RootState } from 'reactApp/store';
import { Button } from 'reactApp/ui/Button/Button';
import { ETabsName } from 'reactApp/ui/TariffSectionNew/TariffSection.constants';
import { formatLocale } from 'reactApp/utils/formatDate';
import { sendDwh } from 'reactApp/utils/ga';
import { formatPrice, getMonthProductPrice } from 'reactApp/utils/priceHelpers';

import { revealPlatformPopup } from '../TariffsPlans/TariffPlansCard/TariffPlansCard.helpers';
import styles from './PromoBlackFriday2025.css';

type PromoBlackFriday2025Props = {
    isIOSPromo?: boolean;
    isAndroidPromo?: boolean;
    isMobilePayAvailable?: boolean;
};

export const PromoBlackFriday2025: React.FC<PromoBlackFriday2025Props> = ({
    isIOSPromo = false,
    isAndroidPromo = false,
    isMobilePayAvailable,
}) => {
    const dispatch = useDispatch();
    const isMobile = EnvironmentSelectors.isMobile();
    const endPromotionDate = formatLocale(parseISO(promotionEndDate), isThisYear(parseISO(promotionEndDate)) ? 'dd MMMM' : 'dd MMMM yyyy');
    const activeTariffTab = useSelector(getMultiTariffActiveTab);

    let productId;
    if (isMobile) {
        productId = blackFriday2025Tariff;
    } else if (activeTariffTab === ETabsName.family) {
        productId =
            isFamilySpaceExpValue && blackFriday2025TariffFamily
                ? blackFriday2025TariffFamily
                : multiTariffSpaceToEachFamilyMember && multiTariffFamilyTariffListEnable
                ? blackFriday2025TariffSpaceEveryone
                : blackFriday2025Tariff;
    } else {
        productId = blackFriday2025Tariff;
    }
    const product = useSelector((state: RootState) => ProductsSelectors.getProductById(state, productId));
    const space = product?.space.value || '1\u00A0ТБ';
    const monthPrice = useMemo(() => {
        if (!product) {
            return null;
        }
        return formatPrice(getMonthProductPrice(product));
    }, [product]);

    useEffect(() => {
        sendDwh({ eventCategory: 'button', action: 'show', dwhData: { name: 'mnz_flight_action_blackfriday_1125' } });
    }, []);

    const handleClick = useCallback(() => {
        sendDwh({ eventCategory: 'button', action: 'click', dwhData: { name: 'mnz_flight_action_blackfriday_1125' } });
        if (productId) {
            if ((isAndroidPromo || isIOSPromo) && !isMobilePayAvailable) {
                revealPlatformPopup(productId, isIOSPromo, isAndroidPromo);
                return;
            }

            dispatch(
                openTariffBuy({
                    productId,
                    checkAnonymous: true,
                    isMobile,
                    paySource: getPaySource('action_button_1tb_bf25'),
                    hideOtherTariffs: true,
                })
            );
        }
    }, [dispatch, isMobile, productId]);

    return (
        <div
            className={classNames(styles.blockPromo, {
                [styles.blockPromoMobile]: isMobile,
            })}
        >
            <div
                className={classNames(styles.leftText, {
                    [styles.leftTextMobile]: isMobile,
                })}
            >
                Акция до&nbsp;{endPromotionDate}
            </div>
            <div className={styles.rightBlock}>
                <img
                    alt="-80%"
                    src={img80}
                    className={classNames(styles.image, {
                        [styles.hideImg]:
                            (isFamilySpaceExpValue || multiTariffSpaceToEachFamilyMember) && activeTariffTab === ETabsName.family,
                        [styles.imageMobile]: isMobile,
                    })}
                />
                <img
                    alt="-81%"
                    src={img81}
                    className={classNames(styles.image, {
                        [styles.hideImg]:
                            isMobile ||
                            activeTariffTab !== ETabsName.family ||
                            !(isFamilySpaceExpValue || multiTariffSpaceToEachFamilyMember),
                    })}
                />
                <Button
                    className={classNames(styles.button, {
                        [styles.buttonMobile]: isMobile,
                    })}
                    theme="vk"
                    onClick={handleClick}
                >
                    Забрать {space} за&nbsp;{monthPrice}&nbsp;₽ в&nbsp;месяц
                </Button>
            </div>
        </div>
    );
};

PromoBlackFriday2025.displayName = 'PromoBlackFriday2025';
